import { Component, Input, Output, EventEmitter } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { FontAwesomeModule } from '@fortawesome/angular-fontawesome';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { faPlus, faTrash, faBolt, faSpinner, faCode, faWandMagicSparkles, faLayerGroup, faFileLines } from '@fortawesome/free-solid-svg-icons';
import { JsonataFieldComponent } from '../jsonata-field/jsonata-field.component';
import { ProcessingOperation, OperationType, OperationScope } from '../../services/processings.service';

@Component({
  selector: 'app-processing-operations',
  standalone: true,
  imports: [CommonModule, FormsModule, FontAwesomeModule, TranslateModule, JsonataFieldComponent],
  templateUrl: './processing-operations.component.html',
  styleUrls: ['./processing-operations.component.scss'],
})
export class ProcessingOperationsComponent {
  icons = { faPlus, faTrash, faBolt, faSpinner, faCode, faWandMagicSparkles, faLayerGroup, faFileLines };

  @Input() operations: ProcessingOperation[] = [];
  @Input() allowTest = true;
  @Output() operationsChange = new EventEmitter<ProcessingOperation[]>();
  @Output() testRequested = new EventEmitter<number>();

  private openParams = new Set<string>();

  constructor(private translate: TranslateService) {}

  addOperation(type: OperationType): void {
    const operation: ProcessingOperation = {
      id: this.newId(),
      description: '',
      type,
      scope: 'field',
      expression: '',
      instruction: '',
    };
    this.operationsChange.emit([...this.operations, operation]);
  }

  removeOperation(index: number): void {
    const next = this.operations.slice();
    next.splice(index, 1);
    this.operationsChange.emit(next);
  }

  setType(index: number, type: OperationType): void {
    this.operations[index].type = type;
    this.emitChange();
  }

  setScope(index: number, scope: OperationScope): void {
    const operation = this.operations[index];
    operation.scope = scope;
    if (scope === 'document' && !operation.target?.trim()) {
      operation.target = `etapa_${index + 1}`;
    }
    this.emitChange();
  }

  scopeOf(operation: ProcessingOperation): OperationScope {
    return operation.scope === 'document' ? 'document' : 'field';
  }

  isNative(operation: ProcessingOperation): boolean {
    return operation.type !== 'jsonata' && operation.type !== 'ai';
  }

  passSummary(operation: ProcessingOperation): string[] {
    const passes = operation.params?.['passes'];
    if (!Array.isArray(passes)) {
      return [];
    }
    const key = (name: string, params?: object) =>
      this.translate.instant(`PROCESSINGS.OPERATIONS.PASS_${name}`, params);
    return passes.map((pass: any) => {
      const labels = pass.labels === 'all' || !pass.labels
        ? key('ALL_LABELS')
        : (Array.isArray(pass.labels) ? pass.labels : [pass.labels]).join(', ');
      const gap = pass.mode === 'all'
        ? key('GAP_BOTH', { x: pass.factor_x ?? 4, y: pass.factor_y ?? 1 })
        : key('GAP_ONE', { factor: pass.factor ?? 4 });
      const sep = pass.sep === ''
        ? key('NO_SEP')
        : key('SEP', { sep: pass.sep ?? ' ' });
      return `${pass.mode ?? 'all'} · ${labels} · ${gap} · ${sep}`;
    });
  }

  toggleParams(operation: ProcessingOperation): void {
    this.openParams.has(operation.id)
      ? this.openParams.delete(operation.id)
      : this.openParams.add(operation.id);
  }

  editingParams(operation: ProcessingOperation): boolean {
    return this.openParams.has(operation.id);
  }

  paramsJson(operation: ProcessingOperation): string {
    return JSON.stringify(operation.params ?? {}, null, 2);
  }

  onParamsJson(operation: ProcessingOperation, value: string): void {
    try {
      operation.params = JSON.parse(value);
      operation.paramsError = undefined;
    } catch {
      operation.paramsError = true;
    }
    this.emitChange();
  }

  isBarrier(operation: ProcessingOperation): boolean {
    return this.scopeOf(operation) === 'document';
  }

  barrierAbove(index: number): ProcessingOperation | null {
    for (let i = index - 1; i >= 0; i -= 1) {
      if (this.isBarrier(this.operations[i])) {
        return this.operations[i];
      }
    }
    return null;
  }

  readsOriginalDocument(index: number): boolean {
    return this.barrierAbove(index) === null;
  }

  emitChange(): void {
    this.operationsChange.emit([...this.operations]);
  }

  defaultTarget(index: number): string {
    return `campo_${index + 1}`;
  }

  barrierLabel(index: number): string {
    const barrier = this.barrierAbove(index);
    return barrier?.target?.trim() || barrier?.description?.trim() || '';
  }

  canTest(operation: ProcessingOperation): boolean {
    if (!operation.target?.trim()) {
      return false;
    }
    if (this.isNative(operation)) {
      return !operation.paramsError;
    }
    if (!operation.description?.trim()) {
      return false;
    }
    return operation.type === 'jsonata'
      ? !!operation.expression?.trim()
      : !!operation.instruction?.trim();
  }

  test(index: number): void {
    this.testRequested.emit(index);
  }

  private newId(): string {
    return 'op_' + Date.now().toString(36) + Math.random().toString(36).slice(2, 7);
  }

  trackById(_index: number, operation: ProcessingOperation): string {
    return operation.id;
  }
}
